import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { CalendarClock, CircleCheck, Compass, Lock, Users, UsersRound, X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  DELETE_OUTING,
  MY_CLUB_MEMBERSHIPS,
  OUTING_CLUB_SHARES,
  OUTING_DETAIL,
  SHARE_OUTING_TO_CLUB,
  UNSHARE_OUTING_FROM_CLUB,
  UPDATE_OUTING,
  type MyClubMembershipsResult,
  type OutingClubSharesResult,
  type OutingDetailResult,
  type UpdateOutingResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import {
  ACTIVITY_ICONS,
  formatDate,
  formatDistance,
  formatElapsed,
  formatElevation,
  formatPace,
} from "../lib/format";
import { useT } from "../lib/i18n";
import { EntityTabs } from "../components/EntityTabs";
import { CommentThread } from "../components/CommentThread";
import { ElevationProfile } from "../components/ElevationProfile";
import { LikeButton } from "../components/LikeButton";
import { Avatar } from "../components/Avatar";
import { PhotoGallery } from "../components/PhotoGallery";
import { ReportButton } from "../components/ReportButton";
import { TrackExportButtons } from "../components/TrackExportButtons";
import { EmptyState, ErrorBox, Spinner, Stars } from "../components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MiniMap } from "../map/MiniMap";
import { toFeature, trackTimeStats } from "../lib/geo";

export function OutingPage({ id }: { id: string }) {
  const { personId } = useAuth();
  const { t, labelizeT, entityName } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["outing", id],
    queryFn: () => gqlRequest<OutingDetailResult>(OUTING_DETAIL, { id }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => gqlRequest(DELETE_OUTING, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feed"] });
      navigate("/feed");
    },
  });

  // Status never auto-flips when the date passes — completing is an explicit
  // owner action (RLS keeps a PLANNED outing owner-only until then).
  const markCompleted = useMutation({
    mutationFn: () => {
      // A planned outing usually carries a future date; completing it must not
      // leave a completed outing dated in the future (a state the edit form
      // rejects), so the date is clamped to today.
      const today = new Date().toISOString().slice(0, 10);
      const date = data?.outing?.date;
      return gqlRequest<UpdateOutingResult>(UPDATE_OUTING, {
        id,
        patch: date && date > today ? { status: "COMPLETED", date: today } : { status: "COMPLETED" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outing", id] });
      queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const outing = data?.outing;
  if (!outing) return <EmptyState>{t("outing.notFound")}</EmptyState>;

  const people = outing.outingPeopleByOutingId.nodes;
  const owner = people.find((p) => p.role === "OWNER");
  const isOwner = personId != null && owner?.personId === personId;
  // Already ordered by sort (OUTING_DETAIL orders the connection), so the
  // first route is the outing's "main" one for map/profile fallbacks.
  const routes = outing.outingRoutesByOutingId.nodes;
  // The outing's own recorded track wins; otherwise fall back to the first
  // (by sort) linked route's MAIN track. Raw geom keeps Z — mainGeom doesn't.
  const track =
    outing.track ??
    routes
      .flatMap((r) => r.route.routeTracksByRouteId.nodes)
      .filter((t) => t.role === "MAIN")
      .map((t) => t.track)[0] ??
    null;
  const timeStats = trackTimeStats(track?.geom, track?.pointTimes);

  const ActivityIcon = ACTIVITY_ICONS[outing.activity];

  const header = (
    <div>
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
        {ActivityIcon && <ActivityIcon aria-hidden className="size-3.5" />}
        {t("outing.kicker")} · {labelizeT(outing.activity)}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{outing.title}</h1>
        <div className="flex items-center gap-2">
          <ReportButton entityKind="OUTING" entityId={outing.id} />
          {isOwner && (
            <>
              {outing.status === "PLANNED" && (
                <Button type="button" onClick={() => markCompleted.mutate()} disabled={markCompleted.isPending}>
                  <CircleCheck aria-hidden className="size-4" />
                  {markCompleted.isPending ? t("common.saving") : t("outing.markCompleted")}
                </Button>
              )}
              <Button asChild variant="outline">
                <Link href={`/outing/${outing.id}/edit`}>{t("outing.edit")}</Link>
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (window.confirm(t("outing.confirmDelete"))) deleteMutation.mutate();
                }}
                disabled={deleteMutation.isPending}
                className="border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
              >
                {deleteMutation.isPending ? t("common.deleting") : t("common.delete")}
              </Button>
            </>
          )}
        </div>
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
        {formatDate(outing.date)}
        {owner?.displayName && (
          <span className="inline-flex items-center gap-1.5">
            · {t("common.by")}
            <Avatar person={{ displayName: owner.displayName, avatarKey: owner.person?.avatarKey }} size="xs" />
            {owner.displayName}
          </span>
        )}
        {outing.rating != null && <Stars rating={outing.rating} />}
        {outing.status === "PLANNED" && (
          <Badge
            title={t("outing.plannedTitle")}
            className="bg-amber-100 font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
          >
            <CalendarClock aria-hidden className="size-3" />
            {labelizeT("PLANNED")}
          </Badge>
        )}
        {outing.visibility === "PRIVATE" && (
          <Badge variant="secondary" title={t("outing.privateTitle")} className="text-muted-foreground">
            <Lock aria-hidden className="size-3" />
            {labelizeT("PRIVATE")}
          </Badge>
        )}
        {outing.visibility === "FOLLOWERS" && (
          <Badge variant="secondary" title={t("outing.followersTitle")} className="text-muted-foreground">
            <Users aria-hidden className="size-3" />
            {labelizeT("FOLLOWERS")}
          </Badge>
        )}
      </p>
      <div className="mt-2">
        <LikeButton
          outingId={outing.id}
          likeCount={outing.likeCount}
          viewerHasLiked={outing.viewerHasLiked}
        />
      </div>
      {isOwner && <ShareToGroup outingId={outing.id} />}
      {deleteMutation.error != null && <ErrorBox error={deleteMutation.error} />}
      {markCompleted.error != null && <ErrorBox error={markCompleted.error} />}
    </div>
  );

  const infoTab = (
    <div className="space-y-4">
      {outing.description && <p className="whitespace-pre-line text-foreground">{outing.description}</p>}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {outing.conditions && <Stat label={t("stat.conditions")} value={labelizeT(outing.conditions)} />}
        {outing.elapsedSeconds != null && <Stat label={t("stat.time")} value={formatElapsed(outing.elapsedSeconds)} />}
        {track && (
          <>
            <Stat label={t("stat.distance")} value={formatDistance(track.distanceM)} />
            <Stat label={t("stat.ascent")} value={formatElevation(track.ascentM)} />
            <Stat label={t("stat.descent")} value={formatElevation(track.descentM)} />
          </>
        )}
        {timeStats && (
          <>
            <Stat label={t("stat.movingTime")} value={formatElapsed(timeStats.movingS)} />
            <Stat label={t("stat.pace")} value={formatPace(timeStats.paceSecPerKm)} />
          </>
        )}
      </dl>
      <div>
        <h3 className="mb-2 text-sm font-semibold text-foreground">{t("outing.party")}</h3>
        <ul className="space-y-1.5 text-sm text-foreground">
          {people.map((p) => (
            <li key={p.id} className="flex items-center gap-2">
              <Avatar person={{ displayName: p.displayName, avatarKey: p.person?.avatarKey }} size="sm" />
              <Link href={`/person/${p.personId}`} className="font-medium text-primary hover:underline">
                {p.displayName ?? t("common.someone")}
              </Link>{" "}
              <span className="text-xs text-muted-foreground">({labelizeT(p.role)})</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const mapFeatures = [
    toFeature(outing.track?.geom ?? null, { id: "own-track" }),
    ...routes.map((r) => toFeature(r.route.mainGeom, { id: r.route.id })),
    ...(outing.track ? [] : [toFeature(outing.point, { id: "pin" })]),
  ];

  const mapTab = mapFeatures.some(Boolean) ? (
    <div className="space-y-3">
      <MiniMap
        features={mapFeatures}
        className="h-96 w-full overflow-hidden rounded-xl border shadow-card"
      />
      {track?.geom && (
        <TrackExportButtons trackId={track.id} geom={track.geom} pointTimes={track.pointTimes} name={outing.title} />
      )}
      <ElevationProfile geom={track?.geom} pointTimes={track?.pointTimes} className="rounded-xl border bg-card p-3 text-card-foreground shadow-card" />
    </div>
  ) : (
    <EmptyState>{t("outing.noGeometry")}</EmptyState>
  );

  const photos = outing.outingPhotos.nodes;
  const photosTab = photos.length ? (
    <PhotoGallery photos={photos} />
  ) : (
    <EmptyState>{t("outing.noPhotos")}</EmptyState>
  );

  const routesTab = routes.length ? (
    <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
      {routes.map((r) => (
        <li key={r.id}>
          <Link
            href={`/route/${r.route.id}`}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
          >
            <Compass aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">
              <span className="flex flex-wrap items-center gap-1.5 font-medium text-foreground">
                {entityName(r.route)}
                {r.style && (
                  <Badge className="bg-primary/10 font-semibold text-primary">{labelizeT(r.style)}</Badge>
                )}
                {r.result && (
                  <Badge variant="secondary" className="font-semibold text-muted-foreground">{r.result}</Badge>
                )}
              </span>
              <span className="block text-xs text-muted-foreground">{r.route.activities.map(labelizeT).join(", ")}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  ) : (
    <EmptyState>{t("outing.noRoutes")}</EmptyState>
  );

  return (
    <EntityTabs
      header={header}
      tabs={[
        { key: "info", label: t("tabs.info"), content: infoTab },
        { key: "photos", label: t("tabs.photos"), badge: photos.length, content: photosTab },
        { key: "map", label: t("tabs.map"), content: mapTab },
        { key: "routes", label: t("tabs.routes"), badge: routes.length, content: routesTab },
        {
          key: "comments",
          label: t("tabs.comments"),
          badge: outing.commentCount,
          content: <CommentThread outingId={outing.id} ownerId={owner?.personId ?? null} />,
        },
      ]}
    />
  );
}

/**
 * Owner-only "share to group" control: chips for the groups the outing is
 * already shared with (each with an unshare ✕) plus a compact select over the
 * owner's remaining ACTIVE memberships. Hidden entirely when the owner is in
 * no groups.
 */
function ShareToGroup({ outingId }: { outingId: string }) {
  const { personId } = useAuth();
  const { t } = useT();
  const queryClient = useQueryClient();

  const memberships = useQuery({
    queryKey: ["my-club-memberships", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });

  const shares = useQuery({
    queryKey: ["outing-club-shares", outingId],
    queryFn: () => gqlRequest<OutingClubSharesResult>(OUTING_CLUB_SHARES, { outingId }),
  });

  const invalidate = (clubId: string) => {
    queryClient.invalidateQueries({ queryKey: ["outing-club-shares", outingId] });
    queryClient.invalidateQueries({ queryKey: ["club", clubId] });
  };

  const share = useMutation({
    mutationFn: (clubId: string) => gqlRequest(SHARE_OUTING_TO_CLUB, { clubId, outingId }),
    onSuccess: (_data, clubId) => invalidate(clubId),
  });

  const unshare = useMutation({
    mutationFn: (clubId: string) => gqlRequest(UNSHARE_OUTING_FROM_CLUB, { clubId, outingId }),
    onSuccess: (_data, clubId) => invalidate(clubId),
  });

  // `club` is null on the row for a hidden club the owner was merely invited
  // to; those aren't shareable targets anyway (status must be ACTIVE).
  const active = (memberships.data?.clubMembers.nodes ?? []).filter(
    (m) => m.status === "ACTIVE" && m.club != null,
  );
  if (active.length === 0) return null;

  const shared = shares.data?.clubOutings.nodes ?? [];
  const sharedClubIds = new Set(shared.map((n) => n.clubId));
  const available = active.filter((m) => !sharedClubIds.has(m.clubId));
  const nameByClubId = new Map(active.map((m) => [m.clubId, m.club!.name]));

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
        <UsersRound aria-hidden className="size-3.5" />
        {t("share.label")}
      </span>
      {shared.map((n) => {
        const name = n.club?.name ?? nameByClubId.get(n.clubId) ?? t("common.unnamed");
        return (
          <Badge key={n.id} variant="outline" className="border-primary/30 bg-primary/5 px-2.5 text-primary">
            <Link href={`/group/${n.clubId}`} className="hover:underline">
              {name}
            </Link>
            <button
              type="button"
              onClick={() => unshare.mutate(n.clubId)}
              disabled={unshare.isPending}
              aria-label={t("share.removeAria", { name })}
              className="transition-colors hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
            >
              <X aria-hidden className="size-3" />
            </button>
          </Badge>
        );
      })}
      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) share.mutate(e.target.value);
          }}
          disabled={share.isPending}
          aria-label={t("share.label")}
          className="rounded-lg border bg-background px-2 py-1 text-xs text-muted-foreground shadow-card transition-colors hover:border-ring/50 focus:border-ring focus:outline-none disabled:pointer-events-none disabled:opacity-50"
        >
          <option value="">{t("share.select")}</option>
          {available.map((m) => (
            <option key={m.clubId} value={m.clubId}>
              {m.club!.name}
            </option>
          ))}
        </select>
      )}
      {(share.error ?? unshare.error) != null && <ErrorBox error={share.error ?? unshare.error} />}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-muted/50 p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-base font-semibold text-foreground">{value}</dd>
    </div>
  );
}
